// Negotiation Credits Service
import { supabase } from '@/integrations/supabase/client';

import type { 
  NegotiationCredit, 
  SellerCreditsStatus, 
  NegotiationReport,
  NegotiationReportEnriched,
  UserCancellationStats,
  CreditLedgerEntry
} from '@/types/negotiationCredits';

// Re-export types for convenience
export type { NegotiationCredit, SellerCreditsStatus, NegotiationReport, NegotiationReportEnriched, UserCancellationStats, CreditLedgerEntry };

// Colunas otimizadas para seller_credits_status
const CREDITS_STATUS_COLUMNS = `
  user_id, display_name, is_verified, verification_expires_at,
  seller_type_name, seller_type_label, negotiations_per_year, priority_bonus,
  recharge_discount_percent, boost_total, boost_plan_total, boost_performance_total, highest_plan_tier,
  total_credits, used_credits, available_credits, is_unlimited
`;

/**
 * Fetch available credits count for current user
 */
export async function fetchAvailableCredits(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { data, error } = await supabase.rpc('get_seller_available_credits' as any, {
    p_user_id: user.id
  });

  if (error) {
    console.error('Error fetching available credits:', error);
    return 0;
  }

  return (data as number) ?? 0;
}

/**
 * Check if buyer can start a new negotiation (no active negotiations)
 */
export async function canBuyerStartNegotiation(): Promise<{
  can_start: boolean;
  reason?: string;
  active_negotiation_id?: string;
}> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { can_start: false, reason: 'Usuário não autenticado' };
  }

  const { data, error } = await supabase.rpc('can_buyer_start_negotiation' as any, {
    p_buyer_id: user.id
  });

  if (error) {
    console.error('Error checking buyer negotiation:', error);
    return { can_start: true }; // Fallback to allow
  }

  return data as { can_start: boolean; reason?: string; active_negotiation_id?: string };
}

/**
 * Confirm completion from one side (buyer or seller)
 */
export async function confirmNegotiationCompletion(
  negotiationId: string,
  role: 'buyer' | 'seller'
): Promise<{
  success: boolean;
  both_confirmed?: boolean;
  credit_consumed?: boolean;
  error?: string;
}> {
  const { data, error } = await supabase.rpc('confirm_negotiation_completion' as any, {
    p_negotiation_id: negotiationId,
    p_role: role
  });

  if (error) {
    console.error('Error confirming completion:', error);
    return { success: false, error: error.message };
  }

  return data as {
    success: boolean;
    both_confirmed?: boolean;
    credit_consumed?: boolean;
    error?: string;
  };
}

/**
 * Cancel negotiation with reason
 */
export async function cancelNegotiationWithReason(
  negotiationId: string,
  reason: string,
  category: string
): Promise<{ success: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Usuário não autenticado' };
  }

  const { data, error } = await supabase.rpc('cancel_negotiation_with_reason' as any, {
    p_negotiation_id: negotiationId,
    p_user_id: user.id,
    p_reason: reason,
    p_category: category
  });

  if (error) {
    console.error('Error cancelling negotiation:', error);
    return { success: false, error: error.message };
  }

  const result = data as { success: boolean; error?: string };

  // Fire-and-forget: log cancellation activity with profile names
  if (result.success) {
    (async () => {
      try {
        // Fetch negotiation to determine role
        const { data: neg } = await supabase
          .from('marketplace_negotiations')
          .select('buyer_id, seller_id')
          .eq('id', negotiationId)
          .single();
        const cancelledByRole = neg && user.id === neg.buyer_id ? 'buyer' : 'seller';
        const { logActivityWithProfile } = await import('@/services/activityLog');
        logActivityWithProfile('negotiation_cancelled', user.id, undefined, negotiationId, { cancelled_by_role: cancelledByRole });
      } catch (e) {
        console.warn('[ActivityLog] Cancel log failed:', e);
      }
    })();
  }

  return result;
}

/**
 * Reopen an expired negotiation
 */
export async function reopenNegotiation(
  negotiationId: string
): Promise<{ success: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Usuário não autenticado' };
  }

  const { data, error } = await supabase.rpc('reopen_negotiation' as any, {
    p_negotiation_id: negotiationId,
    p_user_id: user.id
  });

  if (error) {
    console.error('Error reopening negotiation:', error);
    return { success: false, error: error.message };
  }

  return data as { success: boolean; error?: string };
}

/**
 * Create a report for a negotiation
 */
export async function createNegotiationReport(
  negotiationId: string,
  reportedUserId: string,
  category: string,
  details?: string,
  messageIds?: string[]
): Promise<NegotiationReport | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await (supabase
    .from('negotiation_reports' as any)
    .insert({
      negotiation_id: negotiationId,
      reporter_id: user.id,
      reported_user_id: reportedUserId,
      report_category: category,
      report_details: details || null,
      evidence_message_ids: messageIds || null
    })
    .select()
    .single());

  if (error) {
    console.error('Error creating report:', error);
    throw new Error('Erro ao criar denúncia');
  }

  return data as unknown as NegotiationReport;
}

/**
 * Fetch all pending reports (admin)
 */
export async function fetchPendingReports(): Promise<NegotiationReport[]> {
  const { data, error } = await (supabase
    .from('negotiation_reports' as any)
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false }));

  if (error) {
    console.error('Error fetching pending reports:', error);
    return [];
  }

  return (data || []) as unknown as NegotiationReport[];
}

/**
 * Fetch all reports with enriched profile data (admin)
 */
export async function fetchAllReports(): Promise<NegotiationReportEnriched[]> {
  const { data, error } = await supabase.rpc('fetch_admin_reports_with_profiles' as any);

  if (error) {
    console.error('Error fetching reports:', error);
    return [];
  }

  return (data || []) as unknown as NegotiationReportEnriched[];
}

/**
 * Update report status (admin)
 */
export async function updateReportStatus(
  reportId: string,
  status: string,
  reviewNotes?: string,
  actionTaken?: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const { error } = await (supabase
    .from('negotiation_reports' as any)
    .update({
      status,
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      review_notes: reviewNotes || null,
      action_taken: actionTaken || null
    })
    .eq('id', reportId));

  if (error) throw error;
}

/**
 * Fetch flagged users with excessive cancellations (admin)
 * Uses RPC to include profile data (display_name, avatar_url)
 */
export async function fetchFlaggedCancellations(): Promise<UserCancellationStats[]> {
  const { data, error } = await supabase.rpc('fetch_admin_flagged_cancellations' as any);

  if (error) {
    console.error('Error fetching flagged cancellations:', error);
    return [];
  }

  return (data || []) as unknown as UserCancellationStats[];
}

/**
 * Clear cancellation flag (admin)
 */
export async function clearCancellationFlag(userId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const { error } = await (supabase
    .from('user_cancellation_stats' as any)
    .update({
      flagged_for_review: false,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.id
    })
    .eq('user_id', userId));

  if (error) throw error;
}

/**
 * Grant credits to a user (admin)
 */
export async function grantCredits(
  userId: string,
  credits: number,
  source: 'admin_grant' | 'bonus' | 'plan_purchase',
  notes?: string,
  sellerTypeId?: string,
  validityMonths: number = 12
): Promise<void> {
  const expiresAt = new Date();
  expiresAt.setMonth(expiresAt.getMonth() + validityMonths);

  const { error } = await (supabase
    .from('seller_negotiation_credits' as any)
    .insert({
      user_id: userId,
      credits_total: credits,
      source,
      expires_at: expiresAt.toISOString(),
      notes: notes || null,
      seller_type_id: sellerTypeId || null
    }));

  if (error) throw error;
}

/**
 * Fetch user's credit history
 */
export async function fetchMyCreditHistory(): Promise<NegotiationCredit[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await (supabase
    .from('seller_negotiation_credits' as any)
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false }));

  if (error) {
    console.error('Error fetching credit history:', error);
    return [];
  }

  return (data || []) as unknown as NegotiationCredit[];
}

/**
 * Fetch a specific user's credits status (admin)
 */
export async function fetchUserCreditsStatus(userId: string): Promise<SellerCreditsStatus | null> {
  const { data, error } = await (supabase
    .from('seller_credits_status' as any)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle());

  if (error) {
    console.error('Error fetching user credits status:', error);
    return null;
  }

  return data as unknown as SellerCreditsStatus;
}
